import type { LucideIcon } from "lucide-react";
import {
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  Clock,
  Home,
  Library,
  Megaphone,
  Palette,
  Plug,
  ReceiptText,
  Scale,
  Users,
  Zap
} from "lucide-react";

import { CALENDAR_PATH } from "@/features/calendar/routes";
import { caseWorkspacePath, parseCasePath } from "@/features/caseIde/routes";
import { COMPANIES_PATH } from "@/features/companies/routes";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import { HOME_PATH } from "@/features/dashboard/routes";
import { DESIGN_SYSTEM_PATH } from "@/features/designSystem/routes";
import { AUTOMATIONS_PATH } from "@/features/automations/routes";
import { FIRM_KNOWLEDGE_PATH } from "@/features/firmKnowledge/routes";
import { RESEARCH_PATH } from "@/features/research/routes";
import { TIME_PATH, TIME_REVIEW_PATH } from "@/features/timeTracking/routes";

export type NavItem = {
  /** Stable key; also used as the React key. */
  id: string;
  label: string;
  url: string;
  icon: LucideIcon;
  /** Small badge rendered next to the label (e.g. "BETA"). */
  badge?: string;
  /**
   * When true the item is considered active on any pathname, not just when
   * the URL matches (used for the single-item Individual nav).
   */
  alwaysActive?: boolean;
  /** Active only on this exact path (a sibling item owns the paths under it). */
  exact?: boolean;
  /** Also active on these paths outside its URL. */
  includes?: (path: string) => boolean;
  /** Paths under its URL that a sibling item owns. */
  excludes?: (path: string) => boolean;
  /** A count beside the label (e.g. unreleased time entries). */
  count?: number;
  /** Shows the count in amber: something from an earlier day is waiting. */
  warn?: boolean;
};

/** Time tracking is a firm setting; the sidebar reflects it and the person's count. */
export type TimeNav = {
  enabled: boolean;
  isAdmin: boolean;
  unreleased?: number;
  /** An earlier day still has unreleased entries. */
  carriedOver?: boolean;
};

export type NavSections = {
  /** Primary navigation. */
  main: NavItem[];
  /** Superadmin-only tools; rendered as a separate group at the bottom. */
  internal: NavItem[];
};

export type BuildNavItemsInput = {
  userType: string | undefined;
  role?: string;
  /** The individual's visa (e.g. "O-1A"), used to label their case link. */
  visaLabel?: string;
  uid?: string;
  /** Athena is both flag-gated and entitlement-gated; caller resolves that. */
  hasPlaygroundAccess: boolean;
  time?: TimeNav;
  /** Research shows a New badge until the attorney has opened it. */
  researchNew?: boolean;
};

const EMPTY: NavSections = { main: [], internal: [] };

/** Every case page belongs to Cases, including `/clients/:id/cases/:caseId/...`. */
const isCasePath = (path: string): boolean => parseCasePath(path) !== null;

/**
 * Flat navigation model for the app sidebar. Pure so it can be unit tested;
 * the sidebar component only maps this to shadcn primitives.
 */
export function buildNavItems(input: BuildNavItemsInput): NavSections {
  const {
    userType,
    role,
    visaLabel,
    uid,
    hasPlaygroundAccess,
    time,
    researchNew
  } = input;

  switch (userType) {
    case "Lawyer": {
      const isSuperadmin = role === "superadmin";
      const main: NavItem[] = [
        { id: "home", label: "Home", url: HOME_PATH, icon: Home },
        {
          id: "calendar",
          label: "Calendar",
          url: CALENDAR_PATH,
          icon: CalendarDays,
          badge: "New"
        },
        {
          id: "cases",
          label: "Cases",
          url: "/cases",
          icon: Briefcase,
          includes: isCasePath
        },
        {
          id: "clients",
          label: "Clients",
          url: "/clients",
          icon: Users,
          excludes: isCasePath
        },
        {
          id: "companies",
          label: "Companies",
          url: COMPANIES_PATH,
          icon: Building2
        }
      ];
      if (time?.enabled) {
        main.push({
          id: "time",
          label: "Time",
          url: TIME_PATH,
          icon: Clock,
          exact: true,
          count: time.unreleased || undefined,
          warn: Boolean(time.carriedOver)
        });
      }
      if (time?.isAdmin && time.enabled) {
        main.push({
          id: "billing-review",
          label: "Billing Review",
          url: TIME_REVIEW_PATH,
          icon: ReceiptText
        });
      }
      main.push(
        {
          id: "research",
          label: "Research",
          url: RESEARCH_PATH,
          icon: Scale,
          badge: researchNew ? "New" : undefined
        },
        {
          id: "automations",
          label: "Automations",
          url: AUTOMATIONS_PATH,
          icon: Zap
        },
        {
          id: "firm-knowledge",
          label: "Firm Knowledge",
          url: FIRM_KNOWLEDGE_PATH,
          icon: Library
        },
        {
          id: "connectors",
          label: "Connectors",
          url: CONNECTORS_PATH,
          icon: Plug
        }
      );
      if (hasPlaygroundAccess) {
        main.push({
          id: "athena",
          label: "Athena",
          url: "/athena",
          icon: Bot
        });
      }

      const internal: NavItem[] = isSuperadmin
        ? [
            {
              id: "law-firms",
              label: "Law Firms",
              url: "/admin/law-firms",
              icon: Building2
            },
            {
              id: "announcements",
              label: "Announcements",
              url: "/admin/updates",
              icon: Megaphone
            },
            {
              id: "design-system",
              label: "Design System",
              url: DESIGN_SYSTEM_PATH,
              icon: Palette
            }
          ]
        : [];

      return { main, internal };
    }

    case "Individual":
      if (!uid) return EMPTY;
      return {
        main: [
          {
            id: "my-case",
            label: visaLabel ? `${visaLabel} Case` : "My Case",
            url: caseWorkspacePath({ individualId: uid }),
            icon: Briefcase,
            alwaysActive: true
          }
        ],
        internal: []
      };

    default:
      return EMPTY;
  }
}

/** Active when the pathname is the item URL or nested under it. */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.alwaysActive) return true;
  const url = item.url.replace(/\/+$/, "");
  const path = pathname.replace(/\/+$/, "");
  if (item.excludes?.(path)) return false;
  if (item.includes?.(path)) return true;
  if (!url) return path === "";
  if (item.exact) return path === url;
  return path === url || path.startsWith(`${url}/`);
}
